import type {
  AgencyCreatePayload,
  AgencyListParams,
  AgencyListResponse,
  AgencyResponse,
  AgencySearchParams,
  AgencyUpdatePayload,
  SlugAvailabilityResponse,
} from '../interfaces/agency.interface'
import type {
  AgencyRecentlyUsedListResponse,
  AgencyRecentlyUsedParams,
} from '../interfaces/agency-recently-used.interface'
import { agentsApiSlice } from './agents-api.store'

type AgencyTag = { type: 'Agencies'; id: string }

const API_BASE = '/api/v1/agencies'

const buildParams = (p?: AgencyListParams): string => {
  const params = new URLSearchParams()
  params.append('page', String(p?.page ?? 1))
  params.append('page_size', String(p?.pageSize ?? 20))
  p?.visibilityList?.forEach((v) => {
    params.append('visibility', v)
  })
  if (p?.category) {
    params.append('category', p.category)
  }
  return params.toString()
}

const buildRecentlyUsedParams = (p?: AgencyRecentlyUsedParams): Record<string, number> => ({
  page: p?.page ?? 1,
  page_size: p?.pageSize ?? 10,
})

const listTag: AgencyTag = { type: 'Agencies', id: 'LIST' }
const recentlyUsedTag: AgencyTag = { type: 'Agencies', id: 'RECENTLY_USED' }

const buildListTags = (r: AgencyListResponse | undefined): AgencyTag[] =>
  r ? [listTag, ...r.items.map(({ slug }) => ({ type: 'Agencies' as const, id: slug }))] : [listTag]

const buildRecentlyUsedTags = (r: AgencyRecentlyUsedListResponse | undefined): AgencyTag[] =>
  r
    ? [recentlyUsedTag, ...r.items.map(({ slug }) => ({ type: 'Agencies' as const, id: slug }))]
    : [recentlyUsedTag]

const buildSlugTag = (slug: string): AgencyTag => ({ type: 'Agencies', id: slug })

const buildMutationTags = (slug: string): AgencyTag[] => [
  listTag,
  recentlyUsedTag,
  buildSlugTag(slug),
]

const encodeSlug = (slug: string): string => encodeURIComponent(slug)

const agencyEndpoints = agentsApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAgencies: builder.query<AgencyListResponse, AgencyListParams | undefined>({
      query: (p) => `${API_BASE}?${buildParams(p)}`,
      providesTags: (r) => buildListTags(r),
    }),
    getRecentlyUsedAgencies: builder.query<
      AgencyRecentlyUsedListResponse,
      AgencyRecentlyUsedParams | undefined
    >({
      query: (p) => ({ url: `${API_BASE}/recently-used`, params: buildRecentlyUsedParams(p) }),
      providesTags: (r) => buildRecentlyUsedTags(r),
    }),
    searchAgencies: builder.query<AgencyListResponse, AgencySearchParams>({
      query: (p) => ({
        url: `${API_BASE}/search`,
        params: { q: p.q, page: p.page, page_size: p.pageSize },
      }),
      providesTags: [listTag],
    }),
    checkSlugAvailability: builder.query<SlugAvailabilityResponse, string>({
      query: (slug) => `${API_BASE}/check-slug/${encodeSlug(slug)}`,
    }),
    getAgency: builder.query<AgencyResponse, string>({
      query: (slug) => `${API_BASE}/${encodeSlug(slug)}`,
      providesTags: (_, __, slug) => [buildSlugTag(slug)],
    }),
    getAgencyAny: builder.query<AgencyResponse, string>({
      query: (slug) => `${API_BASE}/${encodeSlug(slug)}/any`,
      providesTags: (_, __, slug) => [buildSlugTag(slug)],
    }),
    createAgency: builder.mutation<AgencyResponse, AgencyCreatePayload>({
      query: (body) => ({ url: API_BASE, method: 'POST', body }),
      invalidatesTags: [listTag, recentlyUsedTag],
    }),
    updateAgency: builder.mutation<AgencyResponse, { slug: string; payload: AgencyUpdatePayload }>({
      query: ({ slug, payload }) => ({
        url: `${API_BASE}/${encodeSlug(slug)}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, __, { slug }) => buildMutationTags(slug),
    }),
    deleteAgency: builder.mutation<void, string>({
      query: (slug) => ({ url: `${API_BASE}/${encodeSlug(slug)}`, method: 'DELETE' }),
      invalidatesTags: (_, __, slug) => buildMutationTags(slug),
    }),
  }),
})

export const {
  useGetAgenciesQuery,
  useGetRecentlyUsedAgenciesQuery,
  useGetAgencyQuery,
  useGetAgencyAnyQuery,
  useSearchAgenciesQuery,
  useCheckSlugAvailabilityQuery,
  useLazyCheckSlugAvailabilityQuery,
  useCreateAgencyMutation,
  useUpdateAgencyMutation,
  useDeleteAgencyMutation,
} = agencyEndpoints
